import React, { useEffect, useState, useCallback } from 'react';
import axios, { AxiosError } from 'axios';
import { getStoredAuthToken } from '../../utils/authStorage';
import ZoningMap from '../../components/admin/ZoningMap';
import ZoningAIPanel from '../../components/admin/ZoningAIPanel';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5001';
const API = `${API_BASE}/api/admin/zoning`;

interface ZoningLaw {
  _id: string;
  jurisdiction: string;
  state: string;
  category: string;
  digitalAllowed: boolean;
  illuminationAllowed: boolean;
  animationAllowed: boolean;
  maxHeightFt?: number;
  maxAreaSqFt?: number;
  setbackFt?: number;
  spacingFt?: number;
  maxDwellSeconds?: number;
  permitRequired: boolean;
  permitFee?: number;
  banned: boolean;
  notes?: string;
  source?: string;
  sourceUrl?: string;
  createdAt?: string;
}

type JurisdictionWorkflowStatus = 'untriaged' | 'reviewed' | 'needs-ordinance' | 'approved';

const QUEUE_STORAGE_KEY = 'tellurvision_zoning_queue_status_v1';

const authHeaders = () => {
  const token = getStoredAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const emptyForm = (): Partial<ZoningLaw> => ({
  jurisdiction: '',
  state: '',
  category: 'commercial',
  digitalAllowed: true,
  illuminationAllowed: true,
  animationAllowed: false,
  permitRequired: true,
  banned: false,
});

const ZoningLawAdmin: React.FC = () => {
  const [laws, setLaws] = useState<ZoningLaw[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<Partial<ZoningLaw>>(emptyForm());
  const [showForm, setShowForm] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [filter, setFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<'all' | 'commercial' | 'mobile'>('all');
  const [queueState, setQueueState] = useState('ALL');
  const [queueSearch, setQueueSearch] = useState('');
  const [queueStatus, setQueueStatus] = useState<Record<string, JurisdictionWorkflowStatus>>({});

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(QUEUE_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as Record<string, JurisdictionWorkflowStatus>;
      setQueueStatus(parsed);
    } catch {
      // ignore corrupted local cache
    }
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(QUEUE_STORAGE_KEY, JSON.stringify(queueStatus));
    } catch {
      // best effort persistence
    }
  }, [queueStatus]);

  const fetchLaws = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(API, { headers: authHeaders() });
      setLaws(data);
    } catch (err) {
      const msg = err instanceof AxiosError ? (err.response?.data as Record<string, string>)?.message : undefined;
      setError(msg || 'Failed to load zoning laws');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLaws(); }, [fetchLaws]);

  // Auto-refresh every 30 seconds for live updates
  useEffect(() => {
    const interval = setInterval(() => { fetchLaws(); }, 30_000);
    return () => clearInterval(interval);
  }, [fetchLaws]);

  const handleSeed = async (force = false) => {
    setSeeding(true);
    try {
      await axios.post(`${API}/seed${force ? '?force=true' : ''}`, {}, { headers: authHeaders() });
      await fetchLaws();
    } catch (err) {
      const msg = err instanceof AxiosError ? (err.response?.data as Record<string, string>)?.message : undefined;
      setError(msg || 'Failed to seed');
    } finally {
      setSeeding(false);
    }
  };

  const handleSave = async () => {
    try {
      if (editingId) {
        await axios.put(`${API}/${editingId}`, form, { headers: authHeaders() });
      } else {
        await axios.post(API, form, { headers: authHeaders() });
      }
      setShowForm(false);
      setEditingId(null);
      setForm(emptyForm());
      await fetchLaws();
    } catch (err) {
      const msg = err instanceof AxiosError ? (err.response?.data as Record<string, string>)?.message : undefined;
      setError(msg || 'Save failed');
    }
  };

  const handleEdit = (law: ZoningLaw) => {
    setEditingId(law._id);
    setForm({ ...law });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this zoning law?')) return;
    try {
      await axios.delete(`${API}/${id}`, { headers: authHeaders() });
      await fetchLaws();
    } catch (err) {
      const msg = err instanceof AxiosError ? (err.response?.data as Record<string, string>)?.message : undefined;
      setError(msg || 'Delete failed');
    }
  };

  /** Build a brief human-readable restriction summary for a zoning law */
  const restrictionSummary = (law: ZoningLaw): string => {
    if (law.banned) return 'Billboards banned in this jurisdiction.';
    const parts: string[] = [];
    parts.push(law.digitalAllowed ? 'Digital allowed' : 'Digital prohibited');
    if (law.maxHeightFt) parts.push(`max ${law.maxHeightFt}′ height`);
    if (law.maxAreaSqFt) parts.push(`${law.maxAreaSqFt} ft² max area`);
    if (law.maxDwellSeconds) parts.push(`${law.maxDwellSeconds}s dwell`);
    if (law.setbackFt) parts.push(`${law.setbackFt}′ setback`);
    if (law.permitRequired && law.permitFee) parts.push(`$${law.permitFee.toLocaleString()} permit`);
    if (law.animationAllowed) parts.push('animation OK');
    return parts.join(' · ');
  };

  const filtered = laws.filter((l) => {
    if (categoryFilter !== 'all' && (l.category || '') !== categoryFilter) return false;
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (l.jurisdiction ?? '').toLowerCase().includes(q) || (l.state ?? '').toLowerCase().includes(q);
  });

  const availableStates = Array.from(new Set(laws.map((l) => (l.state || '').toUpperCase()).filter(Boolean))).sort();
  const queueRows = laws
    .filter((l) => queueState === 'ALL' || (l.state || '').toUpperCase() === queueState)
    .map((l) => ({
      key: `${(l.state || '').toUpperCase()}::${l.jurisdiction || ''}`,
      jurisdiction: l.jurisdiction || '',
      state: (l.state || '').toUpperCase(),
      banned: !!l.banned,
      digitalAllowed: !!l.digitalAllowed,
      status: queueStatus[`${(l.state || '').toUpperCase()}::${l.jurisdiction || ''}`] || 'untriaged',
    }))
    .filter((r) => {
      if (!queueSearch.trim()) return true;
      const q = queueSearch.toLowerCase();
      return r.jurisdiction.toLowerCase().includes(q) || r.state.toLowerCase().includes(q);
    })
    .sort((a, b) => a.jurisdiction.localeCompare(b.jurisdiction));

  const queueCounts = queueRows.reduce(
    (acc, row) => {
      acc.total += 1;
      acc[row.status] += 1;
      return acc;
    },
    {
      total: 0,
      untriaged: 0,
      reviewed: 0,
      'needs-ordinance': 0,
      approved: 0,
    } as Record<JurisdictionWorkflowStatus | 'total', number>,
  );

  const setRowStatus = (rowKey: string, status: JurisdictionWorkflowStatus) => {
    setQueueStatus((prev) => ({
      ...prev,
      [rowKey]: status,
    }));
  };

  const statusBadgeClass: Record<JurisdictionWorkflowStatus, string> = {
    untriaged: 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300',
    reviewed: 'bg-blue-100 text-blue-700',
    'needs-ordinance': 'bg-amber-100 text-amber-700',
    approved: 'bg-emerald-100 text-emerald-700',
  };

  const statusLabel: Record<JurisdictionWorkflowStatus, string> = {
    untriaged: 'Untriaged',
    reviewed: 'Reviewed',
    'needs-ordinance': 'Needs Ordinance',
    approved: 'Approved',
  };

  const Badge: React.FC<{ ok: boolean; label: string }> = ({ ok, label }) => (
    <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${ok ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
      {label}
    </span>
  );

  return (
    <main className="mx-auto max-w-6xl px-4 py-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Zoning Management</h1>
          <p className="text-sm text-slate-600 dark:text-slate-400">Manage billboard zoning laws by jurisdiction. <span className="text-slate-400">Auto-refreshes every 30s.</span></p>
        </div>
        <div className="flex gap-2">
          {laws.length === 0 && (
            <button onClick={() => handleSeed(false)} disabled={seeding} className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50">
              {seeding ? 'Seeding…' : 'Seed US data'}
            </button>
          )}
          {laws.length > 0 && (
            <button onClick={() => { if (window.confirm('This will replace all existing zoning data with fresh US seed data. Continue?')) handleSeed(true); }} disabled={seeding} className="rounded-md bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700 disabled:opacity-50">
              {seeding ? 'Reseeding…' : 'Reseed US data'}
            </button>
          )}
          <button onClick={() => fetchLaws()} className="rounded-md border border-slate-300 dark:border-slate-600 px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50">
            Refresh
          </button>
          <button onClick={() => { setEditingId(null); setForm(emptyForm()); setShowForm(true); }} className="rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800">
            + Add law
          </button>
        </div>
      </div>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">{error}</div>}

      {/* Form modal */}
      {showForm && (
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5 shadow-sm space-y-4">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{editingId ? 'Edit' : 'New'} Zoning Law</h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <input className="rounded border border-slate-300 dark:border-slate-600 px-3 py-2 text-sm" placeholder="Jurisdiction" value={form.jurisdiction || ''} onChange={(e) => setForm({ ...form, jurisdiction: e.target.value })} />
            <input className="rounded border border-slate-300 dark:border-slate-600 px-3 py-2 text-sm" placeholder="State (e.g. TX)" value={form.state || ''} onChange={(e) => setForm({ ...form, state: e.target.value })} />
            <select className="rounded border border-slate-300 dark:border-slate-600 px-3 py-2 text-sm" value={form.category || 'commercial'} onChange={(e) => setForm({ ...form, category: e.target.value })}>
              {['commercial', 'residential', 'industrial', 'mixed-use', 'highway', 'scenic', 'historic'].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <input className="rounded border border-slate-300 dark:border-slate-600 px-3 py-2 text-sm" placeholder="Max height (ft)" type="number" value={form.maxHeightFt ?? ''} onChange={(e) => setForm({ ...form, maxHeightFt: e.target.value ? Number(e.target.value) : undefined })} />
            <input className="rounded border border-slate-300 dark:border-slate-600 px-3 py-2 text-sm" placeholder="Max area (sq ft)" type="number" value={form.maxAreaSqFt ?? ''} onChange={(e) => setForm({ ...form, maxAreaSqFt: e.target.value ? Number(e.target.value) : undefined })} />
            <input className="rounded border border-slate-300 dark:border-slate-600 px-3 py-2 text-sm" placeholder="Setback (ft)" type="number" value={form.setbackFt ?? ''} onChange={(e) => setForm({ ...form, setbackFt: e.target.value ? Number(e.target.value) : undefined })} />
            <input className="rounded border border-slate-300 dark:border-slate-600 px-3 py-2 text-sm" placeholder="Max dwell (sec)" type="number" value={form.maxDwellSeconds ?? ''} onChange={(e) => setForm({ ...form, maxDwellSeconds: e.target.value ? Number(e.target.value) : undefined })} />
            <input className="rounded border border-slate-300 dark:border-slate-600 px-3 py-2 text-sm" placeholder="Permit fee ($)" type="number" value={form.permitFee ?? ''} onChange={(e) => setForm({ ...form, permitFee: e.target.value ? Number(e.target.value) : undefined })} />
            <div className="flex flex-wrap gap-3 items-center col-span-full">
              <label className="flex items-center gap-1.5 text-sm"><input type="checkbox" checked={form.digitalAllowed ?? true} onChange={(e) => setForm({ ...form, digitalAllowed: e.target.checked })} /> Digital</label>
              <label className="flex items-center gap-1.5 text-sm"><input type="checkbox" checked={form.illuminationAllowed ?? true} onChange={(e) => setForm({ ...form, illuminationAllowed: e.target.checked })} /> Illumination</label>
              <label className="flex items-center gap-1.5 text-sm"><input type="checkbox" checked={form.animationAllowed ?? false} onChange={(e) => setForm({ ...form, animationAllowed: e.target.checked })} /> Animation</label>
              <label className="flex items-center gap-1.5 text-sm"><input type="checkbox" checked={form.permitRequired ?? true} onChange={(e) => setForm({ ...form, permitRequired: e.target.checked })} /> Permit req.</label>
              <label className="flex items-center gap-1.5 text-sm text-red-700"><input type="checkbox" checked={form.banned ?? false} onChange={(e) => setForm({ ...form, banned: e.target.checked })} /> Banned</label>
            </div>
            <textarea className="rounded border border-slate-300 dark:border-slate-600 px-3 py-2 text-sm col-span-full" placeholder="Notes" rows={2} value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          <div className="flex gap-2">
            <button onClick={handleSave} className="rounded-md bg-slate-900 px-4 py-2 text-xs font-medium text-white hover:bg-slate-800">Save</button>
            <button onClick={() => { setShowForm(false); setEditingId(null); }} className="rounded-md border border-slate-300 dark:border-slate-600 px-4 py-2 text-xs font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50">Cancel</button>
          </div>
        </div>
      )}

      {/* Jurisdiction workflow queue */}
      <section className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 shadow-sm space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">Jurisdiction queue</h2>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Work each state systematically and mark each jurisdiction as reviewed, needs ordinance text, or approved.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 text-[11px]">
            <span className="rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-1 text-slate-700 dark:text-slate-300">Total: {queueCounts.total}</span>
            <span className="rounded-full bg-blue-100 px-2 py-1 text-blue-700">Reviewed: {queueCounts.reviewed}</span>
            <span className="rounded-full bg-amber-100 px-2 py-1 text-amber-700">Needs Ordinance: {queueCounts['needs-ordinance']}</span>
            <span className="rounded-full bg-emerald-100 px-2 py-1 text-emerald-700">Approved: {queueCounts.approved}</span>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-[140px_1fr]">
          <select
            value={queueState}
            onChange={(e) => setQueueState(e.target.value)}
            className="rounded-md border border-slate-300 dark:border-slate-600 px-3 py-2 text-sm"
          >
            <option value="ALL">All states</option>
            {availableStates.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <input
            className="w-full rounded-md border border-slate-300 dark:border-slate-600 px-3 py-2 text-sm"
            placeholder="Search jurisdiction in queue..."
            value={queueSearch}
            onChange={(e) => setQueueSearch(e.target.value)}
          />
        </div>

        <div className="max-h-72 overflow-auto rounded-lg border border-slate-200 dark:border-slate-700">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-slate-50 dark:bg-slate-900 text-slate-500 dark:text-slate-400">
              <tr>
                <th className="px-3 py-2 text-left">Jurisdiction</th>
                <th className="px-3 py-2 text-left">State</th>
                <th className="px-3 py-2 text-left">Current</th>
                <th className="px-3 py-2 text-left">Set Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {queueRows.map((row) => (
                <tr key={row.key} className="hover:bg-slate-50">
                  <td className="px-3 py-2 font-medium text-slate-900 dark:text-slate-100">
                    {row.jurisdiction}
                    <div className="mt-1 flex gap-1">
                      {row.banned && (
                        <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] text-red-700">Banned</span>
                      )}
                      <span className={`rounded-full px-2 py-0.5 text-[10px] ${row.digitalAllowed ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'}`}>
                        {row.digitalAllowed ? 'Digital OK' : 'No Digital'}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-2">{row.state}</td>
                  <td className="px-3 py-2">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${statusBadgeClass[row.status]}`}>
                      {statusLabel[row.status]}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {(['reviewed', 'needs-ordinance', 'approved'] as JurisdictionWorkflowStatus[]).map((status) => (
                        <button
                          key={status}
                          onClick={() => setRowStatus(row.key, status)}
                          className={`rounded border px-2 py-0.5 text-[10px] ${statusBadgeClass[status]} border-transparent hover:opacity-80`}
                        >
                          {statusLabel[status]}
                        </button>
                      ))}
                      <button
                        onClick={() => setRowStatus(row.key, 'untriaged')}
                        className="rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-[10px] text-slate-600 dark:text-slate-400 hover:bg-slate-50"
                      >
                        Reset
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {queueRows.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-3 py-4 text-center text-slate-400">
                    No jurisdictions match this queue filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Filter */}
      {/* Category + filter */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-0.5 text-xs font-medium">
          {([['all', 'All types'], ['commercial', 'Fixed / Static'], ['mobile', 'Mobile']] as const).map(([val, label]) => (
            <button key={val} onClick={() => setCategoryFilter(val)} className={`rounded-md px-3 py-1.5 transition ${categoryFilter === val ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 shadow-sm' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700'}`}>{label}</button>
          ))}
        </div>
        <input className="flex-1 min-w-[200px] rounded-lg border border-slate-300 dark:border-slate-600 px-3 py-2 text-sm" placeholder="Filter by jurisdiction or state…" value={filter} onChange={(e) => setFilter(e.target.value)} />
      </div>

      {/* Table */}
      {loading ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-50 dark:bg-slate-900 text-slate-500 dark:text-slate-400">
              <tr>
                <th className="px-3 py-2">Jurisdiction</th>
                <th className="px-3 py-2">State</th>
                <th className="px-3 py-2">Category</th>
                <th className="px-3 py-2">Digital</th>
                <th className="px-3 py-2">Max H</th>
                <th className="px-3 py-2">Max Area</th>
                <th className="px-3 py-2">Dwell</th>
                <th className="px-3 py-2">Permit $</th>
                <th className="px-3 py-2 min-w-[220px]">Restrictions</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((law) => (
                <tr key={law._id} className={`${law.banned ? 'bg-red-50/50' : 'bg-white dark:bg-slate-900'} hover:bg-slate-50`}>
                  <td className="px-3 py-2 font-medium text-slate-900 dark:text-slate-100">{law.jurisdiction}</td>
                  <td className="px-3 py-2">{law.state}</td>
                  <td className="px-3 py-2 capitalize">{law.category}</td>
                  <td className="px-3 py-2"><Badge ok={law.digitalAllowed} label={law.digitalAllowed ? 'Yes' : 'No'} /></td>
                  <td className="px-3 py-2">{law.maxHeightFt ? `${law.maxHeightFt}′` : '—'}</td>
                  <td className="px-3 py-2">{law.maxAreaSqFt ? `${law.maxAreaSqFt} ft²` : '—'}</td>
                  <td className="px-3 py-2">{law.maxDwellSeconds ? `${law.maxDwellSeconds}s` : '—'}</td>
                  <td className="px-3 py-2">{law.permitFee ? `$${law.permitFee.toLocaleString()}` : '—'}</td>
                  <td className="px-3 py-2 text-slate-600 dark:text-slate-400 text-[11px] leading-tight max-w-[260px]" title={law.notes || ''}>{restrictionSummary(law)}</td>
                  <td className="px-3 py-2">
                    {law.banned ? <Badge ok={false} label="BANNED" /> : <Badge ok={true} label="Active" />}
                  </td>
                  <td className="px-3 py-2 space-x-2">
                    <button onClick={() => handleEdit(law)} className="text-blue-600 hover:underline">Edit</button>
                    <button onClick={() => handleDelete(law._id)} className="text-red-600 hover:underline">Del</button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={11} className="px-3 py-6 text-center text-slate-400">No zoning laws found. Click "Seed US data" to get started.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-slate-400">{laws.length} zoning law{laws.length !== 1 ? 's' : ''} total</p>

      {/* AI Zoning Tools */}
      <section className="space-y-2">
        <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">AI zoning assistant</h2>
        <p className="text-xs text-slate-500 dark:text-slate-400">Research city/state rules, classify ordinance text, and summarize legal passages.</p>
        <ZoningAIPanel />
      </section>

      {/* Zoning Map */}
      <section className="space-y-2">
        <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">Interactive zoning map</h2>
        <p className="text-xs text-slate-500 dark:text-slate-400">Search by state/city and explore ArcGIS + billboard layers directly on the map.</p>
        <ZoningMap />
      </section>
    </main>
  );
};

export default ZoningLawAdmin;
